
import React, { useState, useEffect, useCallback } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';

interface ContentQueryInputProps {
  value: string;
  onChange: (value: string) => void;
  onValidationChange?: (isValid: boolean) => void;
}

export const ContentQueryInput: React.FC<ContentQueryInputProps> = ({ 
  value, 
  onChange,
  onValidationChange = () => {} 
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const validateQuery = useCallback((query: string): boolean => {
    // Reset error message
    setErrorMessage(null);
    
    // Check for empty query
    if (!query.trim()) {
      setErrorMessage('Query cannot be empty');
      return false;
    }
    
    // Check for unbalanced quotes
    const doubleQuoteCount = (query.match(/"/g) || []).length;
    if (doubleQuoteCount % 2 !== 0) {
      setErrorMessage('Unbalanced quotes in query');
      return false;
    }
    
    // Check for invalid characters like $
    if (query.includes('$')) {
      setErrorMessage('Dollar sign ($) is not allowed in Twitter queries');
      return false;
    }
    
    // Too many parentheses can cause issues
    const openParens = (query.match(/\(/g) || []).length;
    const closeParens = (query.match(/\)/g) || []).length;
    if (openParens !== closeParens) {
      setErrorMessage('Unbalanced parentheses in query');
      return false;
    }
    
    return true;
  }, []);
  
  // Run validation whenever the query changes
  useEffect(() => {
    const isValid = validateQuery(value);
    onValidationChange(isValid);
  }, [value, onValidationChange, validateQuery]);
  
  // Fix: Don't normalize spaces between words anymore, preserve user input
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };
  
  // Parse and visualize the query operators with improved badge rendering
  const renderQueryTerms = () => {
    if (!value) return null;
    
    // Split by spaces but preserve quoted phrases
    const terms = [];
    let currentTerm = '';
    let inQuotes = false;
    
    for (let i = 0; i < value.length; i++) {
      const char = value[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
        currentTerm += char;
      } else if (char === ' ' && !inQuotes) {
        if (currentTerm) {
          terms.push(currentTerm);
          currentTerm = '';
        }
      } else {
        currentTerm += char;
      }
    }
    
    // Add the last term
    if (currentTerm) {
      terms.push(currentTerm);
    }
    
    return (
      <div className="flex flex-wrap gap-1 mt-2">
        {terms.map((term, index) => {
          // Handle operators with special styling
          if (term.toUpperCase() === 'OR') {
            return <Badge key={index} variant="outline" className="bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700">{term}</Badge>;
          } else if (term.toUpperCase() === 'AND') {
            return <Badge key={index} variant="outline" className="bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700">{term}</Badge>;
          } else if (term === '-') {
            return <Badge key={index} variant="outline" className="bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700">{term}</Badge>;
          } else if (term.startsWith('"') || term.endsWith('"')) {
            return <Badge key={index} variant="outline" className="bg-purple-100 dark:bg-purple-900/30 border-purple-300 dark:border-purple-700">{term}</Badge>;
          } else if (term.includes(':')) {
            return <Badge key={index} variant="outline" className="bg-amber-100 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700">{term}</Badge>;
          } else if (term.trim()) {
            return <Badge key={index} variant="outline">{term}</Badge>;
          }
          return null;
        }).filter(Boolean)}
      </div>
    );
  };
  
  return (
    <div>
      <Textarea
        value={value}
        onChange={handleChange}
        placeholder="Enter search query using Twitter syntax (e.g., keyword OR another -filter:retweets)"
        className={`resize-y min-h-[100px] ${errorMessage ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
      />
      
      {errorMessage && (
        <p className="text-red-500 text-sm mt-1">{errorMessage}</p>
      )}
      
      {renderQueryTerms()}
    </div>
  );
};
